import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import Hidden from '@material-ui/core/Hidden';
import Container from '@material-ui/core/Container';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import {
  IStateList,
  ListComponent,
  TableCellSortable,
} from 'components/Abstract/List';
import Toolbar from 'components/Layout/Toolbar';
import TableWrapper from 'components/Shared/TableWrapper';
import { WithStyles } from 'decorators/withStyles';
import { IDocument } from 'interfaces/document';
import { IEntity } from 'interfaces/entity';
import { IPaginationParams } from 'interfaces/pagination';
import React, { Fragment } from 'react';
import { of } from 'rxjs';
import RxOp from 'rxjs-operators';
import entityService from 'services/entity';
import selectedVentureService from 'services/profile';
import reportService from 'services/report';

import ListItem from './ListItem';

interface IState extends IStateList<any> {
  current?: IDocument;
  formOpened?: boolean;
  entities?: IEntity[];
  ventureId?: number;
  reportURL?: string;
}
interface IProps {
  classes?: any;
}

@WithStyles((theme) => ({
  footer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}))
export default class SLAReport extends ListComponent<IProps, IState> {
  timer: NodeJS.Timeout = null;

  constructor(props: {}) {
    super(props, 'entityId');
    this.state = {
      ...this.state,
      entities: [] as IEntity[],
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentWillUnmount() {
    this && this.timer && clearTimeout(this.timer);
  }

  loadData = (params: Partial<IPaginationParams> = {}) => {
    this.setState({ loading: true, error: null });

    selectedVentureService
      .getSelectedVenture()
      .pipe(RxOp.logError(), RxOp.bindComponent(this))
      .subscribe(
        (selection) => {
          if (selection) {
            entityService
              .fetchEntitiesGraph(selection.id)
              .pipe(RxOp.logError(), RxOp.bindComponent(this))
              .subscribe(
                (entities) => {
                  this.setState({ entities: entities.data });
                },
                (err) => this.setError(err)
              );
            reportService
              .report({
                params: this.mergeParams(params),
                ventureId: selection.id,
                reportType: 'sla',
              })
              .pipe(
                RxOp.logError(),
                RxOp.bindComponent(this),
                RxOp.tap(() => this.setState({ ventureId: selection.id })),
                RxOp.map((items) => this.setPaginatedData(items)),
                RxOp.catchError((error) => of(this.setError(error)))
              )
              .subscribe();
          }
        },
        (err) => this.setError(err)
      );
  };

  includes = (item: string, term: string) =>
    item.toLocaleLowerCase().includes(term.toLocaleLowerCase());

  filter = () => {
    const { term } = this.state;
    const metrics = this.state.allItems.map((item) => ({
      ...item,
      filtered: !this.includes(item.name, term),
    }));
    this.setAllItems(metrics);
  };

  handleChangeTerm = (term: string) => {
    if (this.state.loading) return;

    this.setState({ term });
    clearTimeout(this.timeoutTerm);

    if (term && term.length < 3) return;

    this.timeoutTerm = setTimeout(() => this.filter(), 500);
  };

  exportReport = () => {
    const { ventureId } = this.state;
    if (!ventureId) return;

    this.setState({ loading: true });
    reportService
      .exportXLS({ ventureId, reportType: 'sla' })
      .pipe(
        RxOp.logError(),
        RxOp.bindComponent(this),
        RxOp.catchError((err) => of({}))
      )
      .subscribe(
        (data) => {
          this.setState({
            reportURL: data.reportURL ? data.reportURL : undefined,
            loading: false,
          });
          this.timer = setTimeout(
            () =>
              this.setState({
                reportURL: undefined,
                loading: false,
              }),
            2500
          );
        },
        (err) => this.setError(err)
      );
  };

  render() {
    const { items, entities, reportURL, loading, ventureId } = this.state;

    return (
      <Fragment>
        <Toolbar
          title="Relatório de SLA"
          breadcrumbs={[{ title: 'Relatório' }]}
        />
        {reportURL && <iframe hidden title={reportURL} src={reportURL} />}
        <Container>
          <Card>
            {this.renderLoader()}
            <CardContent>
              <Grid container alignItems="center" justify="space-between">
                <Grid item xs sm={6} lg={4}>
                  {this.renderSearch()}
                </Grid>
                <Grid item xs="auto">
                  <Button
                    color="primary"
                    variant="contained"
                    size="large"
                    disabled={loading || !ventureId}
                    aria-controls="exportar-relatorio"
                    aria-haspopup="false"
                    onClick={this.exportReport}
                  >
                    Exportar <Hidden xsDown> relatório</Hidden>
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <TableWrapper minWidth={1600}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="entityId"
                          >
                            Módulo
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="tabId"
                          >
                            Aba
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="subTabId"
                          >
                            Subaba
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="name"
                          >
                            Documento
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="filename"
                          >
                            Arquivo
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="uploadDate"
                          >
                            Upload
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="validationDate"
                          >
                            Validação
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="firstName"
                          >
                            Usuário
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="status"
                          >
                            Status
                          </TableCellSortable>
                          <TableCellSortable
                            {...this.sortableProps}
                            column="sla"
                            align="right"
                          >
                            Sla
                          </TableCellSortable>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {this.renderEmptyAndErrorMessages(11)}
                        {entities &&
                          entities.length > 0 &&
                          items
                            .filter((file) => !file.filtered)
                            .map((file) => (
                              <ListItem
                                key={file.id || file.documentId}
                                entities={entities}
                                file={file}
                              />
                            ))}
                      </TableBody>
                    </Table>
                  </TableWrapper>
                  {this.renderTablePagination()}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Container>
      </Fragment>
    );
  }
}
